import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import api from '../services/api';

interface DocumentUploadProps {
  open: boolean;
  onClose: () => void;
  dealId: string;
  onUploadComplete: () => void;
  onUploadInitiated: (documentId: string) => void;
}

interface UploadProgress {
  progress: number;
  status: 'idle' | 'uploading' | 'processing' | 'completed' | 'error';
  message: string;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({
  open,
  onClose,
  dealId,
  onUploadComplete,
  onUploadInitiated,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState<string>('CIM');
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
    progress: 0,
    status: 'idle',
    message: '',
  });
  const [dragActive, setDragActive] = useState(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const [passwordPrompt, setPasswordPrompt] = useState<{ file: File; fileName: string } | null>(null);
  const [password, setPassword] = useState('');
  const [isCheckingPassword, setIsCheckingPassword] = useState(false);

  const handlePasswordSubmit = async () => {
    if (!passwordPrompt) return;
    setIsCheckingPassword(true);
    try {
      await handleUploadWithPassword(passwordPrompt.file, password);
      setPasswordPrompt(null);
      setPassword('');
    } catch (error) {
      console.error('Password verification failed:', error);
    } finally {
      setIsCheckingPassword(false);
    }
  };

  const handlePasswordCancel = () => {
    setPasswordPrompt(null);
    setPassword('');
    setSelectedFile(null);
  };

  const handleUploadWithPassword = async (file: File, filePassword: string) => {
    setUploadProgress({
      progress: 0,
      status: 'uploading',
      message: 'Uploading document...',
    });

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', documentType);
      if (filePassword) {
        formData.append('password', filePassword);
      }

      const progressInterval = setInterval(() => {
        setUploadProgress(prev => ({
          ...prev,
          progress: Math.min(prev.progress + 10, 90),
        }));
      }, 200);

      const response = await api.post(
        `/api/v1/deals/${dealId}/documents/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      clearInterval(progressInterval);

      if (response.status === 200 && response.data.status === 'processing') {
        setUploadProgress({
          progress: 100,
          status: 'processing',
          message: 'Document uploaded successfully! AI processing started...',
        });
        onUploadInitiated(response.data.document_id);
        handleClose();
      } else {
        throw new Error(response.data.message || 'Upload failed');
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        setUploadProgress({
          progress: 0,
          status: 'error',
          message: 'Incorrect password. Please try again.',
        });
        setPasswordPrompt({ file, fileName: file.name });
      } else {
        setUploadProgress({
          progress: 0,
          status: 'error',
          message: error.response?.data?.detail || 'Upload failed. Please try again.',
        });
      }
    }
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (isValidFileType(file)) {
        setSelectedFile(file);
      } else {
        setUploadProgress({
          progress: 0,
          status: 'error',
          message: 'Please select a valid file type (PDF, DOC, DOCX)',
        });
      }
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (isValidFileType(file)) {
        setSelectedFile(file);
        setUploadProgress({ progress: 0, status: 'idle', message: '' });
      } else {
        setUploadProgress({
          progress: 0,
          status: 'error',
          message: 'Please select a valid file type (PDF, DOC, DOCX)',
        });
      }
    }
  };

  const isValidFileType = (file: File): boolean => {
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    return allowedTypes.includes(file.type);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    await handleUploadWithPassword(selectedFile, '');
  };

  const handleClose = () => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    setSelectedFile(null);
    setDocumentType('CIM');
    setUploadProgress({ progress: 0, status: 'idle', message: '' });
    onClose();
  };

  useEffect(() => {
    const currentPollingRef = pollingRef.current;
    return () => {
      if (currentPollingRef) {
        clearInterval(currentPollingRef);
      }
    };
  }, []);

  const getStatusColor = () => {
    switch (uploadProgress.status) {
      case 'completed': return 'success';
      case 'error': return 'error';
      case 'processing': return 'info';
      default: return 'info';
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Upload CIM Document</Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload a Confidential Information Memorandum (CIM) or other deal document. 
            Our AI will automatically extract financial data, identify risks, and generate due diligence questions.
          </Typography>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Document Type</InputLabel>
            <Select
              value={documentType}
              label="Document Type"
              onChange={(e) => setDocumentType(e.target.value)}
            >
              <MenuItem value="CIM">Confidential Information Memorandum (CIM)</MenuItem>
              <MenuItem value="Financial_Statements">Financial Statements</MenuItem>
              <MenuItem value="Management_Presentation">Management Presentation</MenuItem>
              <MenuItem value="Due_Diligence_Report">Due Diligence Report</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>

          {/* File Upload Area */}
          <Box
            sx={{
              border: 2,
              borderColor: dragActive ? 'primary.main' : 'grey.300',
              borderStyle: 'dashed',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              backgroundColor: dragActive ? 'action.hover' : 'background.paper',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover',
              },
            }}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => document.getElementById('file-input')?.click()}
          >
            <input
              id="file-input"
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            {selectedFile ? (
              <Box>
                <DescriptionIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  {selectedFile.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatFileSize(selectedFile.size)}
                </Typography>
              </Box>
            ) : (
              <Box>
                <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  Drop your document here or click to browse
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Supported formats: PDF, DOC, DOCX (Max 50MB)
                </Typography>
              </Box>
            )}
          </Box>

          {/* Upload Progress */}
          {uploadProgress.status !== 'idle' && (
            <Box sx={{ mt: 3 }}>
              <Alert severity={getStatusColor()} sx={{ mb: 2 }}>
                {uploadProgress.message}
              </Alert>
              
              {(uploadProgress.status === 'uploading' || uploadProgress.status === 'processing') && (
                <LinearProgress 
                  variant="determinate" 
                  value={uploadProgress.progress} 
                  sx={{ mb: 1 }}
                />
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={uploadProgress.status === 'uploading'}>
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={!selectedFile || uploadProgress.status === 'uploading' || uploadProgress.status === 'processing'}
          startIcon={<CloudUploadIcon />}
        >
          {uploadProgress.status === 'uploading' ? 'Uploading...' : 
           uploadProgress.status === 'processing' ? 'Processing...' : 'Upload & Process'}
        </Button>
      </DialogActions>

      {/* Password Dialog */}
      <Dialog open={!!passwordPrompt} onClose={handlePasswordCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Password Required</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The PDF "{passwordPrompt?.fileName}" is password protected. Please enter the password to continue.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="password"
            label="PDF Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && password) {
                handlePasswordSubmit();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePasswordCancel}>Cancel</Button>
          <Button
            onClick={handlePasswordSubmit}
            variant="contained"
            disabled={!password || isCheckingPassword}
            startIcon={isCheckingPassword ? <CircularProgress size={20} /> : null}
          >
            {isCheckingPassword ? 'Verifying...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default DocumentUpload;
